import React, { useMemo } from 'react';
import { Shot, Template, TemplateElement } from '@bynder-studio/render-core';
import { List, InlineBanner } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { flattenTree } from '~/common/editor/helpers/elementtree';
import { convertFrameToTime } from '~/common/editor/timeline/helpers/frame-to-time-converter';
import {
    getElementIdsForSceneRange,
    getGlobalEditableElements,
    getSceneElementStateMap,
    getShotDisabledElementIds,
    getVideoDesignSceneRows,
} from 'packages/pages/design/sidebar/shots/utils';
import { useReviewStatus } from 'packages/pages/design/sidebar/shots/useReviewStatus';
import ShotItemGlobal from 'packages/pages/design/sidebar/shots/ShotItemGlobal';
import ShotReviewBanner from 'packages/pages/design/sidebar/shots/ShotReviewBanner';
import useVariations from 'packages/pages/design/hooks/useVariations';
import useDesign from 'packages/pages/design/hooks/useDesign';
import useAccessRights from 'packages/hooks/useAccessRights';
import features from '~/configs/features';
import SceneRows from './SceneRows';

type SceneRangeElement = {
    id: number | string;
    startFrame?: number;
    children?: SceneRangeElement[];
    properties?: Record<string, unknown>;
    toObject?: () => Record<string, unknown>;
};

type SceneRangeTemplateElement = {
    id: number;
    properties: {
        startFrame: number;
    } & Record<string, unknown>;
    children: SceneRangeTemplateElement[];
} & Record<string, unknown>;

const normalizeSceneRangeElement = (element: SceneRangeElement): SceneRangeTemplateElement => {
    const serializedElement = (element.toObject?.() ?? element) as SceneRangeElement;
    const { children, properties, ...rest } = serializedElement as SceneRangeElement & Record<string, unknown>;

    return {
        ...rest,
        id: Number(serializedElement.id),
        properties: {
            ...(properties || {}),
            startFrame: Number(serializedElement.startFrame ?? properties?.startFrame ?? 0),
        },
        children: Array.isArray(children) ? children.map(normalizeSceneRangeElement) : [],
    };
};

type Props = {
    template: Template;
    currentPageIndex: number;
    elements: TemplateElement[];
    shots: Shot[];
    selectedShot: Shot;
    selectedShotIndex: number;
    isToolbarDisabled: boolean;
    isLoading: boolean;
};

const ShotVideoDesign = ({
    template,
    currentPageIndex,
    elements,
    shots,
    selectedShot,
    selectedShotIndex,
    isToolbarDisabled,
    isLoading,
}: Props) => {
    const { translate } = useTranslate();
    const { isDisabledByReview } = useReviewStatus();
    const { deletedAssets } = useVariations();
    const { creativeModel } = useDesign();
    const { isPlatformAllowed } = useAccessRights();

    const { frameRate } = template.pages[currentPageIndex];
    const currentModel = creativeModel.getModels()[creativeModel.getCurrentPageIndex()];
    const modelElements = currentModel?.getAllElementsRecursively?.() || [];
    const modelSceneElements = currentModel?.getElements?.().map(normalizeSceneRangeElement) || [];
    const previousShot = shots[selectedShotIndex - 1];

    const isDynamicElementSelectionEnabled = useMemo(
        () => isPlatformAllowed([features.DYNAMIC_ELEMENT_SELECTION]),
        [isPlatformAllowed],
    );

    const elementStatesById = getSceneElementStateMap(modelElements);

    const allowedIds = selectedShot
        ? getElementIdsForSceneRange(modelSceneElements as unknown as TemplateElement[], {
              fromFrame: selectedShot.startFrame,
              toFrame: selectedShot.startFrame + selectedShot.duration - 1,
          })
        : new Set<number>();

    const disabledIds = getShotDisabledElementIds({
        elements,
        currentShot: selectedShot,
        previousShot,
    });

    const sceneRows = getVideoDesignSceneRows({
        elements,
        elementStatesById,
        isDynamicElementSelectionEnabled,
        allowedIds,
        disabledIds,
    });

    const globalEditableElements = useMemo(
        () => getGlobalEditableElements(flattenTree(elements) as TemplateElement),
        [elements],
    );

    return (
        <List>
            <List.Item
                subtext={
                    <>
                        {convertFrameToTime(selectedShot?.startFrame || 0, frameRate)} -{' '}
                        {convertFrameToTime(selectedShot?.startFrame + selectedShot?.duration, frameRate)}
                    </>
                }
            >
                {translate('design.sidebar.scene.globals.duration.label', {
                    name: selectedShot ? selectedShot.name : '',
                })}
            </List.Item>
            <ShotReviewBanner />
            {!!deletedAssets.length && (
                <InlineBanner variant="error">{translate('design.sidebar.scene.replace-missing-asset')}</InlineBanner>
            )}
            {globalEditableElements.map((element, idx) => (
                <ShotItemGlobal
                    key={element.id}
                    id={element.id}
                    isDisabled={isDisabledByReview || isToolbarDisabled}
                    isLast={idx === globalEditableElements.length - 1}
                />
            ))}
            <SceneRows
                rows={sceneRows}
                isToolbarDisabled={isToolbarDisabled}
                isLoading={isLoading}
                isDisabledByReview={isDisabledByReview}
            />
        </List>
    );
};

export default ShotVideoDesign;
